import React, {useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {
    makeGetAnimalsInGroup,
    makeGetWeightingEvents,
} from "../../../selectors/animalDocumentsSelectors";
import {localMomentToUTC} from "../../../utils/DateTimeUtils";
import useAnimalGroupInfo from "../ideal-charts/useAnimalGroupInfo";
import useDomain from "../ideal-charts/useDomain";
import useReferenceLines from "../ideal-charts/useReferenceLines";
import WeightingContainer from "./WeightingContainer";
import {EventTypes} from "../../../constans/eventTypes";
import {groupBy} from "lodash";
import {shouldIncludeInWeightingChart} from "../ideal-charts/utils/eventFilters";

const WeightingGroupChart = () => {
    const groupInfo = useAnimalGroupInfo();
    const props = useRef({getDeletedAnimals: true});

    const getAnimalsInGroup = useMemo(() => makeGetAnimalsInGroup(), []);
    const getWeightingEvents = useMemo(() => makeGetWeightingEvents(), []);

    const weightingEvents = useSelector((state) =>
        getWeightingEvents(state, props.current)
    );

    const animalsInGroup = useSelector((state) =>
        getAnimalsInGroup(state, props.current)
    );

    const domain = useDomain();

    const referenceLines = useReferenceLines(domain, "lineAxis");

    const weightDict = useMemo(() => {
        console.log("weightDictweightDictweightDict");
        const dictionary = {};
        for (const event of weightingEvents) {
            if (!shouldIncludeInWeightingChart(event)) {
                continue;
            }

            const utcTIME = +localMomentToUTC(event?.EvTime).startOf("day");
            if (!dictionary[utcTIME]) dictionary[utcTIME] = [];
            const animal = animalsInGroup.find((a) => a.AnmID === event.AnmID);
            dictionary[utcTIME].push({
                [event.EvCode]: event?.EvData?.Weight || 0,
                weight: event?.EvData?.Weight || 0,
                Files: event?.EvData?.Files || [],
                AnmCnt: event?.EvData?.AnmCnt || event?.AnmCnt || 1,
                AnmID: event?.AnmID,
                PlcmntID: animal?.DelPlcmntID || animal?.PlcmntID,
                Auto:
                    event.EvCode === EventTypes.WEIGHTING
                        ? event.EvData?.OperID?.toLowerCase?.() === "system"
                        : false,
            });
        }
        // #12171 - tl;dr wywalamy automatyczne ważenie i zastępujemy średnią ważoną
        for (const utcTIME in dictionary) {
            const entries = dictionary[utcTIME].filter((entry) => entry.Auto);

            if (entries.length === 0) {
                continue;
            }

            // grupowanie po PlcmntID
            const groupedByPlcmnt = groupBy(
                entries,
                (entry) => entry.PlcmntID || "unknown"
            );

            const newEntries = [];

            for (const [plcmntID, group] of Object.entries(groupedByPlcmnt)) {
                let sumWeighted = 0;
                let sumCount = 0;

                for (const entry of group) {
                    sumWeighted += entry.weight * entry.AnmCnt;
                    sumCount += entry.AnmCnt;
                }

                const weightedAverage =
                    sumCount > 0 ? sumWeighted / sumCount : 0;

                newEntries.push({
                    [`${EventTypes.WEIGHTING}_AT`]: weightedAverage,
                    weight: weightedAverage,
                    Files: [],
                    AnmCnt: sumCount,
                    AnmID: `all-in-${plcmntID}`,
                    PlcmntID: plcmntID,
                    Auto: false,
                });
            }

            // zachowujemy tylko ręczne wpisy + dodajemy średnie z automatycznych
            dictionary[utcTIME] = dictionary[utcTIME]
                .filter((entry) => !entry.Auto)
                .concat(newEntries);
        }
        return dictionary;
    }, [weightingEvents, animalsInGroup]);

    const weightingProps = useMemo(() => {
        return {
            weightDict,
            chartDomain: domain,
            referenceLines,
            ...groupInfo,
        };
    }, [weightDict, domain, referenceLines, groupInfo]);

    return (
        <WeightingContainer
            weightingProps={weightingProps}
            events={weightingEvents}
            isGroup
        />
    );
};

export default React.memo(WeightingGroupChart);
